import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, Card, Alert, ProgressBar, InputGroup, Badge, Tabs, Tab, Modal } from "react-bootstrap";
import { Formik } from "formik";
import { useNavigate, useParams, Link } from "react-router-dom";
import Dashboardheader from "./dashboardheader";
import Sidebar from "./sidebar";
import toast from "react-hot-toast";
import * as Yup from 'yup';
import { BsArrowLeftCircleFill, BsShield, BsInfoCircle, BsX, BsFileText } from "react-icons/bs";
import { FaUserShield, FaCode, FaLevelUpAlt, FaUserEdit } from "react-icons/fa";
import { MdEdit, MdSave, MdCancel, MdInfo, MdWarning } from "react-icons/md";
import config from "../../config/config";
import { rbacService } from '../../services';

import '../componentCss/webform.css';
import '../componentCss/sidebar.css';
import '../componentCss/healthcare-modern.css';

// Enhanced validation schema aligned with backend RoleUpdate model
const editUserGroupSchema = Yup.object().shape({
  display_name: Yup.string()
    .required("Role name is required")
    .min(2, "Must be at least 2 characters")
    .max(100, "Cannot be longer than 100 characters"),
  role_id: Yup.string()
    .required("Role code is required")
    .min(3, "Must be at least 3 characters")
    .max(50, "Cannot be longer than 50 characters")
    .matches(/^[a-z0-9_-]+$/, "Code can only contain lowercase letters, numbers, underscores, and hyphens"),
  description: Yup.string()
    .required("Description is required")
    .min(10, "Must be at least 10 characters")
    .max(500, "Cannot be longer than 500 characters"),
  hierarchy_level: Yup.number()
    .required("Hierarchy level is required")
    .min(1, "Hierarchy level must be between 1 and 10")
    .max(10, "Hierarchy level must be between 1 and 10"),
  is_active: Yup.boolean()
});

const EditUserGroup = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [userGroupDetails, setUserGroupDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [existingRoles, setExistingRoles] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Fetch role details and existing roles
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('🔍 Starting fetchData for role ID:', id);
        
        // Fetch role details and existing roles in parallel with proper error handling
        const [roleResult, existingResult] = await Promise.allSettled([
          rbacService.getRoleById(id),
          rbacService.getRoles()
        ]);
        
        console.log('📊 API Results:', { roleResult, existingResult });

        // Handle role details result
        if (roleResult.status === 'fulfilled' && roleResult.value && roleResult.value.success) {
          console.log('✅ Role API Success:', roleResult.value);
          const roleData = {
            ...roleResult.value.data,
            display_name: roleResult.value.data.display_name || roleResult.value.data.name,
            role_id: roleResult.value.data.role_id || roleResult.value.data.id,
            hierarchy_level: roleResult.value.data.hierarchy_level || 1,
            is_active: roleResult.value.data.is_active !== undefined ? roleResult.value.data.is_active : true
          };

          console.log('📝 Parsed Role Data:', roleData);
          setUserGroupDetails(roleData);
          setOriginalData(roleData);
        } else {
          console.error('❌ Role API Failed:', roleResult);
          const errorMsg = roleResult.status === 'fulfilled' 
            ? (roleResult.value?.message || "Failed to load role details")
            : (roleResult.reason?.message || "Failed to load role details");
          toast.error(errorMsg);
          return; // Don't continue if role loading failed
        }

        // Handle existing roles result (non-critical)
        if (existingResult.status === 'fulfilled' && existingResult.value && existingResult.value.success) {
          console.log('✅ Existing Roles API Success');
          const filteredRoles = existingResult.value.data?.filter(role => role.role_id !== id) || [];
          setExistingRoles(filteredRoles);
        } else {
          // Silently handle existing roles failure - not critical for basic functionality
          console.warn("⚠️ Failed to load existing roles (non-critical):", existingResult.reason || existingResult.value);
          setExistingRoles([]);
        }
        
        console.log('🎉 fetchData completed successfully');
      } catch (error) {
        console.error("💥 Unexpected error in fetchData:", error);
        toast.error("An unexpected error occurred while loading role details");
        setUserGroupDetails(null);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  // Helper functions
  const getHierarchyLevelColor = (level) => {
    if (level <= 3) return 'success';
    if (level <= 6) return 'warning';
    if (level <= 8) return 'info';
    return 'danger';
  };
  console.log("userGroupDetails", userGroupDetails)
  const isRoleCodeTaken = (roleId, currentRoleId) => {
    return existingRoles.some(role => role.role_id === roleId && role.role_id !== currentRoleId);
  };

  const detectChanges = (values) => {
    const changed = JSON.stringify(values) !== JSON.stringify(originalData);
    setHasChanges(changed);
    return changed;
  };

  // Track form values for change detection
  const [currentFormValues, setCurrentFormValues] = React.useState(null);

  // Detect changes whenever form values change
  React.useEffect(() => {
    if (currentFormValues && originalData) {
      detectChanges(currentFormValues);
    }
  }, [currentFormValues, originalData]);

  const handleFieldChange = (fieldName, value, setFieldValue) => {
    setFieldValue(fieldName, value);
    setHasChanges(true);
  };

  const getStatusBadge = (isActive) => {
    return (
      <Badge bg={isActive ? 'success' : 'secondary'}>
        {isActive ? 'Active' : 'Inactive'}
      </Badge>
    );
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);

    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setUpdateProgress(prev => {
        if (prev < 90) return prev + 10;
        return prev;
      });
    }, 200);

    try {

      // Construct payload matching backend's RoleUpdate schema
      const payload = {
        role_id: values.role_id,
        name: values.display_name,
        display_name: values.display_name,
        description: values.description,
        hierarchy_level: values.hierarchy_level,
        is_active: values.is_active
      };

      const res = await rbacService.updateRole(id, payload);

      clearInterval(progressInterval);
      setUpdateProgress(100);

      if (res.success) {
        toast.success(
          <div>
            <strong>Success!</strong><br />
            {res.message || "Role updated successfully!"}
          </div>
        );
        setHasChanges(false);
        setIsEditMode(false);
        setTimeout(() => {
          navigate(`${config.baseUrl}user-group`);
        }, 2000);
      } else {
        setIsLoading(false);
        setUpdateProgress(0);
        toast.error(
          <div>
            <strong>Error!</strong><br />
            {res.message || "Failed to update role."}
          </div>
        );
      }
    } catch (error) {
      clearInterval(progressInterval);
      setIsLoading(false);
      setUpdateProgress(0);

      // Handle RBAC/Permission errors (403) explicitly
      const status = error?.response?.status;
      if (status === 403) {
        toast.error(error?.response?.data?.detail || 'You do not have permission to update roles.');
        return;
      }

      toast.error(
        <div>
          <strong>Failed!</strong><br />
          {error.message || "An error occurred while updating the role."}
        </div>
      );
    }
  };

  const handleDelete = async () => {
    try {
      const res = await rbacService.deleteRole(id);

      if (res.success) {
        toast.success("Role deleted successfully!");
        setShowDeleteModal(false);
        setTimeout(() => {
          navigate(`${config.baseUrl}user-group`);
        }, 1500);
      } else {
        toast.error(res.message || "Failed to delete role");
      }
    } catch (error) {
      // Handle RBAC/Permission errors (403) explicitly
      const status = error?.response?.status;
      if (status === 403) {
        toast.error(error?.response?.data?.detail || 'You do not have permission to delete roles.');
        return;
      }

      toast.error("An error occurred while deleting the role");
    }
  };

  const resetForm = (resetForm) => {
    resetForm();
    setHasChanges(false);
    setIsEditMode(false);
  };

  if (!userGroupDetails) {
    return (
      <>
        <Dashboardheader />
        <div className="wrapper healthcare-container">
          <Sidebar />
          <div id="content">
            <Container fluid className="p-4">
              <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                <div className="text-center">
                  <span className="spinner-border spinner-border-lg text-primary" role="status" aria-hidden="true"></span>
                  <p className="mt-3 text-muted">Loading role details...</p>
                </div>
              </div>
            </Container>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Dashboardheader />
      <div className="wrapper">
        <Sidebar />
        <div id="content">
          <Container fluid className="p-4">
            <Row>
              <Col lg={12} md={12} sm={12}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="headtitle mb-1">
                      <FaUserEdit className="me-2" />
                      Edit Role
                    </h5>
                    <small className="text-muted">Update role information and permissions</small>
                  </div>
                  <div className="d-flex gap-2">
                    {hasChanges && (
                      <Alert variant="warning" className="p-2 mb-0 d-flex align-items-center">
                        <MdWarning className="me-1" />
                        <small>You have unsaved changes</small>
                      </Alert>
                    )}
                    <Link to={`${config.baseUrl}user-group`}>
                      <button className="healthcare-btn healthcare-btn-secondary">
                        <BsArrowLeftCircleFill size={16} />
                        Back to Roles
                      </button>
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col lg={12} md={12} sm={12}>
                {/* Role Overview Card */}
                <div className="healthcare-card mb-4">
                  <div className="p-4">
                    <Row className="align-items-center">
                      <Col md={2} className="text-center">
                        <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center mx-auto" style={{ width: '80px', height: '80px' }}>
                          <FaUserShield size={32} className="text-white" />
                        </div>
                        {getStatusBadge(userGroupDetails.is_active)}
                      </Col>
                      <Col md={10}>
                        <Row>
                          <Col md={6}>
                            <h6 className="mb-1">{userGroupDetails.display_name || userGroupDetails.name}</h6>
                            <p className="text-muted mb-2">
                              <FaCode className="me-1" />
                              {userGroupDetails.role_id}
                            </p>
                            <p className="text-muted mb-0">{userGroupDetails.description}</p>
                          </Col>
                          <Col md={6}>
                            <div className="text-end">
                              <div className="mb-2">
                                <Badge bg={getHierarchyLevelColor(userGroupDetails.hierarchy_level)} className="me-2">
                                  <FaLevelUpAlt className="me-1" />
                                  Level {userGroupDetails.hierarchy_level}
                                </Badge>
                              </div>
                              <small className="text-muted">
                                <strong>Created:</strong> {userGroupDetails.created_at || 'Unknown'}
                              </small>
                            </div>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </div>
                </div>

                <div className="healthcare-card">
                  <div className="p-4">
                    <Formik
                      enableReinitialize={true}
                      initialValues={{
                        display_name: userGroupDetails.display_name || userGroupDetails.name || "",
                        role_id: userGroupDetails.role_id || "",
                        description: userGroupDetails.description || "",
                        hierarchy_level: userGroupDetails.hierarchy_level || 1,
                        is_active: userGroupDetails.is_active !== undefined ? userGroupDetails.is_active : true
                      }}
                      validationSchema={editUserGroupSchema}
                      onSubmit={handleSubmit}
                    >
                      {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        setFieldValue,
                        resetForm,
                        isSubmitting,
                      }) => {
                        // Update current form values for change detection
                        setCurrentFormValues(values);

                        const isCodeTaken = values.role_id && isRoleCodeTaken(values.role_id, userGroupDetails.role_id);

                        return (
                          <Form onSubmit={handleSubmit}>
                            {/* Progress Bar */}
                            {isLoading && (
                              <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span className="text-muted">Updating role...</span>
                                  <span className="text-primary">{updateProgress}%</span>
                                </div>
                                <ProgressBar now={updateProgress} variant="primary" style={{ height: '6px' }} />
                              </div>
                            )}

                            {/* Tabbed Interface */}
                            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
                              <Tab eventKey="basic" title={<><BsShield className="me-2" />Basic Information</>}>
                                <div className="pt-3">
                                  <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h6 className="mb-0">Role Details</h6>
                                    {!isEditMode ? (
                                      <button
                                        onClick={() => setIsEditMode(true)}
                                        className="healthcare-btn healthcare-btn-primary"
                                      >
                                        <MdEdit size={16} />
                                        Edit Role
                                      </button>
                                    ) : (
                                      <div className="d-flex gap-2">
                                        <button
                                          type="submit"
                                          disabled={isLoading || isCodeTaken || !hasChanges}
                                          className="healthcare-btn healthcare-btn-success"
                                        >
                                          <MdSave size={16} />
                                          Save Changes
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => setIsEditMode(false)}
                                          className="healthcare-btn healthcare-btn-secondary"
                                        >
                                          <MdCancel size={16} />
                                          Cancel
                                        </button>
                                      </div>
                                    )}
                                  </div>

                                  <Row className="mb-4">
                                    <Form.Group as={Col} md="6">
                                      <Form.Label>Role Name *</Form.Label>
                                      <InputGroup>
                                        <InputGroup.Text>
                                          <FaUserShield size={16} />
                                        </InputGroup.Text>
                                        <Form.Control
                                          type="text"
                                          name="display_name"
                                          onChange={(e) => handleFieldChange('display_name', e.target.value, setFieldValue)}
                                          onBlur={handleBlur}
                                          value={values.display_name}
                                          disabled={!isEditMode}
                                          isInvalid={touched.display_name && !!errors.display_name}
                                          isValid={!errors.display_name && touched.display_name && values.display_name}
                                          placeholder="e.g., Senior Physician"
                                        />
                                      </InputGroup>
                                      <Form.Control.Feedback type="invalid">
                                        {errors.display_name}
                                      </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group as={Col} md="6">
                                      <Form.Label>Role Code *</Form.Label>
                                      <InputGroup>
                                        <InputGroup.Text>
                                          <FaCode size={16} />
                                        </InputGroup.Text>
                                        <Form.Control
                                          type="text"
                                          name="role_id"
                                          onChange={(e) => handleFieldChange('role_id', e.target.value, setFieldValue)}
                                          onBlur={handleBlur}
                                          value={values.role_id}
                                          disabled={!isEditMode}
                                          isInvalid={(touched.role_id && !!errors.role_id) || isCodeTaken}
                                          isValid={!errors.role_id && touched.role_id && values.role_id && !isCodeTaken}
                                          placeholder="e.g., senior_physician"
                                        />
                                      </InputGroup>
                                      <Form.Control.Feedback type="invalid">
                                        {isCodeTaken ? 'This role code is already taken' : errors.role_id}
                                      </Form.Control.Feedback>
                                      <Form.Text className="text-muted">
                                        <MdInfo size={14} className="me-1" />
                                        Changing the role code may affect system integrations
                                      </Form.Text>
                                    </Form.Group>
                                  </Row>

                                  <Row className="mb-4">
                                    <Form.Group as={Col} md="8">
                                      <Form.Label>Description *</Form.Label>
                                      <InputGroup>
                                        <InputGroup.Text>
                                          <BsFileText size={16} />
                                        </InputGroup.Text>
                                        <Form.Control
                                          as="textarea"
                                          rows={3}
                                          name="description"
                                          onChange={(e) => handleFieldChange('description', e.target.value, setFieldValue)}
                                          onBlur={handleBlur}
                                          value={values.description}
                                          disabled={!isEditMode}
                                          isInvalid={touched.description && !!errors.description}
                                          isValid={!errors.description && touched.description && values.description}
                                          placeholder="Describe the responsibilities and scope of this role..."
                                        />
                                      </InputGroup>
                                      <Form.Control.Feedback type="invalid">
                                        {errors.description}
                                      </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group as={Col} md="4">
                                      <Form.Label>Hierarchy Level *</Form.Label>
                                      <InputGroup>
                                        <InputGroup.Text>
                                          <FaLevelUpAlt size={16} />
                                        </InputGroup.Text>
                                        <Form.Control
                                          type="number"
                                          name="hierarchy_level"
                                          onChange={(e) => handleFieldChange('hierarchy_level', parseInt(e.target.value), setFieldValue)}
                                          onBlur={handleBlur}
                                          value={values.hierarchy_level}
                                          disabled={!isEditMode}
                                          isInvalid={touched.hierarchy_level && !!errors.hierarchy_level}
                                          isValid={!errors.hierarchy_level && touched.hierarchy_level && values.hierarchy_level}
                                          min="1"
                                          max="10"
                                        />
                                        <InputGroup.Text>
                                          <Badge bg={getHierarchyLevelColor(values.hierarchy_level)}>
                                            Level {values.hierarchy_level}
                                          </Badge>
                                        </InputGroup.Text>
                                      </InputGroup>
                                      <Form.Control.Feedback type="invalid">
                                        {errors.hierarchy_level}
                                      </Form.Control.Feedback>
                                    </Form.Group>
                                  </Row>

                                  <Row className="mb-4">
                                    <Form.Group as={Col} md="6">
                                      <Form.Label>Status</Form.Label>
                                      <div className="d-flex align-items-center gap-3">
                                        <Form.Check
                                          type="switch"
                                          id="role-status"
                                          name="is_active"
                                          label={values.is_active ? "Active" : "Inactive"}
                                          checked={values.is_active}
                                          onChange={(e) => handleFieldChange('is_active', e.target.checked, setFieldValue)}
                                          disabled={!isEditMode}
                                        />
                                        {getStatusBadge(values.is_active)}
                                      </div>
                                      <Form.Text className="text-muted">
                                        Inactive roles cannot be assigned to users
                                      </Form.Text>
                                    </Form.Group>
                                  </Row>

                                  {/* Existing Roles Reference */}
                                  {existingRoles.length > 0 && (
                                    <Alert variant="info" className="mb-4">
                                      <BsInfoCircle className="me-2" />
                                      <strong>Other Hierarchy Levels:</strong>
                                      <div className="mt-2">
                                        {existingRoles
                                          .sort((a, b) => b.hierarchy_level - a.hierarchy_level)
                                          .slice(0, 5)
                                          .map((role, index) => (
                                            <Badge
                                              key={index}
                                              bg={getHierarchyLevelColor(role.hierarchy_level)}
                                              className="me-2 mb-1"
                                            >
                                              {role.display_name || role.name} (Level {role.hierarchy_level})
                                            </Badge>
                                          ))
                                        }
                                        {existingRoles.length > 5 && (
                                          <span className="text-muted">... and {existingRoles.length - 5} more</span>
                                        )}
                                      </div>
                                    </Alert>
                                  )}
                                </div>
                              </Tab>

                              <Tab eventKey="danger" title={<><BsX className="me-2" />Danger Zone</>}>
                                <div className="pt-3">
                                  <Alert variant="danger">
                                    <h6 className="alert-heading">
                                      <MdWarning className="me-2" />
                                      Danger Zone
                                    </h6>
                                    <p>These actions are irreversible. Please proceed with caution.</p>
                                    <hr />
                                    <button
                                      onClick={() => setShowDeleteModal(true)}
                                      className="healthcare-btn healthcare-btn-danger"
                                    >
                                      <BsX size={16} />
                                      Delete Role
                                    </button>
                                  </Alert>
                                </div>
                              </Tab>
                            </Tabs>

                            {/* Form Actions */}
                            <div className="border-top pt-4 mt-4">
                              <Row>
                                <Col>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      {hasChanges && (
                                        <Alert variant="warning" className="p-2 mb-0 d-flex align-items-center">
                                          <MdWarning className="me-2" />
                                          <small>You have unsaved changes</small>
                                        </Alert>
                                      )}
                                    </div>
                                    <div className="d-flex gap-3">
                                      <Link to={`${config.baseUrl}user-group`}>
                                        <button
                                          disabled={isLoading}
                                          className="healthcare-btn healthcare-btn-secondary"
                                        >
                                          Back to List
                                        </button>
                                      </Link>
                                      {isEditMode && (
                                        <>
                                          <button
                                            onClick={() => resetForm(resetForm)}
                                            disabled={isLoading}
                                            className="healthcare-btn healthcare-btn-secondary"
                                          >
                                            Reset Changes
                                          </button>
                                          <button
                                            type="submit"
                                            disabled={isLoading || !hasChanges || isCodeTaken}
                                            className="healthcare-btn healthcare-btn-primary"
                                          >
                                            {isLoading ? (
                                              <>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                Updating...
                                              </>
                                            ) : (
                                              <>
                                                <MdSave size={16} />
                                                Update Role
                                              </>
                                            )}
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          </Form>
                        );
                      }}
                    </Formik>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>

          {/* Delete Confirmation Modal */}
          <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>
                <MdWarning className="text-danger me-2" />
                Confirm Deletion
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Alert variant="danger">
                <strong>Warning:</strong> This action cannot be undone.
              </Alert>
              <p>
                Are you sure you want to delete the role <strong>"{userGroupDetails.display_name || userGroupDetails.name}"</strong>?
              </p>
              <p className="text-muted small">
                This will remove the role from the system and may affect users who currently have this role assigned.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="healthcare-btn healthcare-btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="healthcare-btn healthcare-btn-danger"
              >
                <BsX className="me-1" />
                Delete Role
              </button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default EditUserGroup;
